import { HTTPError } from 'ky';

export type ValidationErrorDetail = {
  ctx?: Record<string, unknown>;
  loc: Array<string | number>;
  msg: string;
  type: string;
};

export type ErrorResponseBody =
  | { detail: ValidationErrorDetail[] } // 422 validation errors
  | { detail: Record<string, unknown> } // Other structured detail payloads (e.g. 412 with fileCount)
  | string // Plain text error
  | null; // Failed to parse

export class VestigasHTTPError extends HTTPError {
  /**
   * Parsed response body containing error details.
   */
  public parsedBody: ErrorResponseBody = null;

  private constructor(originalError: HTTPError, parsedBody: ErrorResponseBody) {
    // Call parent with the same response, request, options from original error
    super(originalError.response, originalError.request, originalError.options);

    // Copy over properties from original error
    this.name = 'VestigasHTTPError';
    this.message = originalError.message;
    this.stack = originalError.stack;

    // Add our parsed body
    this.parsedBody = parsedBody;
  }

  /**
   * Creates a VestigasHTTPError from an HTTPError, parsing the response body.
   */
  static async create(error: HTTPError): Promise<VestigasHTTPError> {
    try {
      const contentType = error.response.headers.get('content-type');

      if (!contentType?.includes('application/json')) {
        return new VestigasHTTPError(
          error,
          (await error.response.text()) || null,
        );
      }

      const jsonBody: unknown = await error.response.json();

      if (
        !jsonBody ||
        typeof jsonBody !== 'object' ||
        Array.isArray(jsonBody)
      ) {
        return new VestigasHTTPError(error, null);
      }

      if ('detail' in jsonBody) {
        if (Array.isArray(jsonBody.detail)) {
          return new VestigasHTTPError(error, {
            detail: jsonBody.detail as ValidationErrorDetail[],
          });
        }

        if (typeof jsonBody.detail === 'string') {
          return new VestigasHTTPError(error, jsonBody.detail);
        }

        if (
          jsonBody.detail !== null &&
          typeof jsonBody.detail === 'object' &&
          !Array.isArray(jsonBody.detail)
        ) {
          return new VestigasHTTPError(error, {
            detail: jsonBody.detail as Record<string, unknown>,
          });
        }
      }

      if ('message' in jsonBody && typeof jsonBody.message === 'string') {
        return new VestigasHTTPError(error, jsonBody.message);
      }
    } catch {
      // If parsing fails, parsedBody stays null
    }

    return new VestigasHTTPError(error, null);
  }
}
